import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import Page from 'components/shared/Page';
import styled from 'styled-components';
import Grid from 'components/UI/Grid';
import Input from 'components/UI/Input';
import { useStores } from 'stores/hooks/hooks';
import { Menubar as PrimeReactMenuBar } from 'primereact/menubar';
import { MenuItem } from 'primereact/menuitem';
import Sensors from './Sensors/Sensors';
import ApiKeys from './ApiKeys/ApiKeys';
import NotificationTemplateText from './NotificationTemplateText/NotificationTemplateText';
import NotificationTypes from './NotificationTypes/NotificationTypes';
import UserProfilesDefault from './UserProfilesDefault/UserProfilesDefault';
import Agents from './Agents/Agents';

const StyledPrimeReactMenuBar = styled(PrimeReactMenuBar)`
`;

const ContentPanel = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
  background: var(--surface-section);
  padding: 1rem;
  z-index: 1;
  border: 0 none;
  color: #495057;
  border-bottom-right-radius: 3px;
  border-bottom-left-radius: 3px;
`;

const TabViewContainer = styled.div`
  flex-direction: column;
  position: relative;
  display: flex;
  width: 100%;
  /* margin-top: 1rem; */
`;

const StyledInput = styled(Input)`
  background-color: ${(p) => p.theme.palette.background.default};
`;

export const TreeContainer = styled.div`
  height: calc(100vh - 106px);
  margin: 1rem;
  margin-right: 0;
  overflow: hidden;
`;

const enum StaticDataTab {
  SensorsTable = 0,
  ApiKeysTable = 1,
  UserProfilesDefaultTable = 2,
  NotificationTypesTable = 3,
  NotificationTemplateTextTable = 4,
  AgentsTable = 5,
}

const StaticData: FC = observer(() => {
  const { t } = useTranslation('staticData');
  const { systemsStore } = useStores();
  const [activeTab, setActiveTab] = useState<StaticDataTab>(
    StaticDataTab.SensorsTable,
  );

  const handleTabChange = useCallback((tabId: StaticDataTab) => {
    setActiveTab(tabId);
  }, []);

  const menuItems: MenuItem[] = useMemo(
    () => [
      {
        label: t('sensor_types'),
        icon: 'pi pi-fw pi-database',
        command: () => handleTabChange(StaticDataTab.SensorsTable),
        className:
          activeTab === StaticDataTab.SensorsTable ? 'p-highlight' : '',
      },
      {
        label: t('api_keys'),
        icon: 'pi pi-fw pi-key',
        command: () => handleTabChange(StaticDataTab.ApiKeysTable),
        className:
          activeTab === StaticDataTab.ApiKeysTable ? 'p-highlight' : '',
      },
      {
        label: t('user_profiles_default'),
        icon: 'pi pi-fw pi-user',
        command: () => handleTabChange(StaticDataTab.UserProfilesDefaultTable),
        className:
          activeTab === StaticDataTab.UserProfilesDefaultTable
            ? 'p-highlight'
            : '',
      },
      {
        label: t('notification_types'),
        icon: 'pi pi-fw pi-bell',
        command: () => handleTabChange(StaticDataTab.NotificationTypesTable),
        className:
          activeTab === StaticDataTab.NotificationTypesTable
            ? 'p-highlight'
            : '',
      },
      {
        label: t('notification_template_text'),
        icon: 'pi pi-fw pi-file',
        command: () =>
          handleTabChange(StaticDataTab.NotificationTemplateTextTable),
        className:
          activeTab === StaticDataTab.NotificationTemplateTextTable
            ? 'p-highlight'
            : '',
      },
      {
        label: t('agents'),
        icon: 'pi pi-fw pi-users',
        command: () => handleTabChange(StaticDataTab.AgentsTable),
        className: activeTab === StaticDataTab.AgentsTable ? 'p-highlight' : '',
      },
    ],
    [t, activeTab, handleTabChange],
  );

  const handleSearchChange = useCallback(() => {
    // TODO: implement search for each tab
    // resellersStore.setGlobalFilter((event.target.value || '').trim());
  }, []);

  const renderContent = () => {
    switch (activeTab) {
      case StaticDataTab.SensorsTable:
        return <Sensors />;
      case StaticDataTab.ApiKeysTable:
        return <ApiKeys />;
      case StaticDataTab.UserProfilesDefaultTable:
        return <UserProfilesDefault />;
      case StaticDataTab.NotificationTypesTable:
        return <NotificationTypes />;
      case StaticDataTab.NotificationTemplateTextTable:
        return <NotificationTemplateText />;
      case StaticDataTab.AgentsTable:
        return <Agents />;
      default:
        return null;
    }
  };

  useEffect(() => {
    systemsStore.getSensorTypes();
  }, [systemsStore]);

  return (
    <Page>
      <TabViewContainer>
        <StyledPrimeReactMenuBar model={menuItems} />
        <ContentPanel>{renderContent()}</ContentPanel>
      </TabViewContainer>
    </Page>
  );
});

export default StaticData;
